import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { NavLink } from 'react-router-dom';
import 'date-fns-tz';
import { cn } from '../../utils/formatters.js';
import '@radix-ui/react-label';
import '../ui/alert.js';
import '@radix-ui/react-avatar';
import '../ui/badge.js';
import 'lucide-react';
import 'react';
import 'react-day-picker';
import '../ui/button.js';
import '../ui/chart.js';
import '@radix-ui/react-checkbox';
import '@radix-ui/react-collapsible';
import 'cmdk';
import '@radix-ui/react-dialog';
import '@radix-ui/react-popover';
import 'vaul';
import '@radix-ui/react-dropdown-menu';
import '@radix-ui/react-progress';
import '../ui/radio-group.js';
import '@radix-ui/react-select';
import '../ui/time-input.js';
import '@radix-ui/react-separator';
import '../ui/sidebar.js';
import { Skeleton } from '../ui/skeleton.js';
import 'react-toastify';
import '@radix-ui/react-switch';
import '@radix-ui/react-tabs';
import '@radix-ui/react-tooltip';
import '@clerk/clerk-react';
import '../../lib/toast.js';
import '../../contexts/AppContext.js';
import '../../contexts/OrganizationContext.js';
import 'axios';
import '@tanstack/react-query';
import './FormPageLayout.js';
import '../form/RichTextEditor.js';
import '../crud/CrudDetailView.js';
import '../crud/CrudFormView.js';
import '../../contexts/SidebarContext.js';
import '../../contexts/ThemeContext.js';
import '@dnd-kit/core';
import '@dnd-kit/sortable';
import '@dnd-kit/utilities';

const GRID_TEXTURE_STYLE = {
    backgroundImage: [
        'linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px)',
        'linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)',
    ].join(', '),
    backgroundSize: '32px 32px',
};
const NOISE_SVG_DATA_URI = 'url("data:image/svg+xml,%3Csvg viewBox=%270 0 256 256%27 xmlns=%27http://www.w3.org/2000/svg%27%3E%3Cfilter id=%27noise%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.8%27 numOctaves=%274%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23noise)%27/%3E%3C/svg%3E")';
const NOISE_TEXTURE_STYLE = {
    backgroundImage: NOISE_SVG_DATA_URI,
};
/**
 * Hero banner + sticky tab bar + content area used on entity detail pages.
 *
 * Renders a dark blue hero with a subtle grid + noise texture, an image panel
 * (optional), the entity category label, title, and optional meta/badges
 * slots. A sticky NavLink tab bar follows the hero, and `children` are rendered
 * inside the neutral content area below.
 *
 * During `isLoading`, a matching skeleton is rendered in place of each section
 * so the layout doesn't shift.
 */
function DetailPageHero({ isLoading, category, title, image, meta, badges, tabs, basePath, children, className, }) {
    const CategoryIcon = category.icon;
    const FallbackIcon = image?.fallbackIcon;
    const imageShape = image?.shape ?? 'rect';
    const objectFit = image?.objectFit ?? 'cover';
    // For "contain" images we paint a light background behind the image so the
    // letterboxing reads as intentional padding rather than a transparent gap.
    const containBackground = image?.containBackground ?? '#ffffff';
    const imageContainerClasses = imageShape === 'avatar'
        ? 'w-24 h-24 rounded-full overflow-hidden flex-shrink-0 bg-white/10'
        : imageShape === 'square'
            ? 'w-44 h-44 rounded-lg overflow-hidden flex-shrink-0 bg-white/10'
            : 'w-full sm:w-64 h-44 rounded-lg overflow-hidden flex-shrink-0 bg-white/10';
    const fallbackIconSize = imageShape === 'avatar' ? 'h-10 w-10' : 'h-12 w-12';
    const imgClassName = objectFit === 'contain' ? 'w-full h-full object-contain' : 'w-full h-full object-cover';
    const imgWrapperStyle = objectFit === 'contain' && image?.url ? { backgroundColor: containBackground } : undefined;
    return (jsx("div", { className: cn('flex-1 min-h-0 overflow-auto', className), children: isLoading ? (jsx(DetailPageHeroSkeleton, { hasImage: !!image, imageShape: imageShape, tabs: tabs })) : (jsxs(Fragment, { children: [jsxs("div", { className: "bg-blue-900 relative overflow-hidden", children: [jsx("div", { className: "absolute inset-0 pointer-events-none", style: GRID_TEXTURE_STYLE }), jsx("div", { className: "absolute inset-0 opacity-[0.12] pointer-events-none", style: NOISE_TEXTURE_STYLE }), jsxs("div", { className: "relative z-[2] px-4 sm:px-6 py-6 sm:py-8 flex flex-col sm:flex-row gap-4 sm:gap-6", children: [image && FallbackIcon && (jsx("div", { className: imageContainerClasses, style: imgWrapperStyle, children: image.videoUrl ? (jsx("video", { src: image.videoUrl, poster: image.url ?? undefined, controls: true, className: "w-full h-full object-cover", "aria-label": image.alt })) : image.url ? (jsx("img", { src: image.url, alt: image.alt, className: imgClassName })) : (jsx("div", { className: "w-full h-full flex items-center justify-center", children: jsx(FallbackIcon, { className: `${fallbackIconSize} text-white/30` }) })) })), jsxs("div", { className: "flex flex-col justify-center gap-1.5 min-w-0", children: [jsxs("div", { className: "flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-white/70", children: [jsx(CategoryIcon, { className: "h-3.5 w-3.5" }), category.label] }), jsx("h1", { className: "text-2xl font-semibold tracking-tight text-white", children: title }), meta && (jsx("div", { className: "flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/70", children: meta })), badges && jsx("div", { className: "flex flex-wrap items-center gap-2 mt-1", children: badges })] })] })] }), tabs && tabs.length > 0 && basePath && (jsx("nav", { className: "sticky top-0 z-10 flex items-center gap-4 sm:gap-8 border-b bg-card px-4 sm:px-6 overflow-x-auto scrollbar-none", children: tabs.map((tab) => {
                        const TabIcon = tab.icon;
                        return (jsxs(NavLink, { to: `${basePath}/${tab.path}`, className: ({ isActive }) => `flex items-center gap-1.5 pb-4 pt-4 text-sm font-medium -mb-px border-b-2 transition-colors whitespace-nowrap ${isActive
                                ? 'text-primary border-primary'
                                : 'text-muted-foreground hover:text-foreground border-transparent'}`, children: [jsx(TabIcon, { className: "h-4 w-4 shrink-0" }), tab.label] }, tab.path));
                    }) })), jsx("div", { className: "bg-background flex-1 p-4 sm:p-6", children: children })] })) }));
}
function DetailPageHeroSkeleton({ hasImage, imageShape, tabs }) {
    const imageSkeletonClass = imageShape === 'avatar'
        ? 'w-24 h-24 rounded-full bg-white/10 flex-shrink-0'
        : imageShape === 'square'
            ? 'w-44 h-44 rounded-lg bg-white/10 flex-shrink-0'
            : 'w-full sm:w-64 h-44 rounded-lg bg-white/10';
    return (jsxs(Fragment, { children: [jsx("div", { className: "bg-blue-900", children: jsxs("div", { className: "px-4 sm:px-6 py-6 sm:py-8 flex flex-col sm:flex-row gap-4 sm:gap-6", children: [hasImage && jsx(Skeleton, { className: imageSkeletonClass }), jsxs("div", { className: "flex flex-col justify-center gap-3 min-w-0", children: [jsx(Skeleton, { className: "h-3 w-24 bg-white/10" }), jsx(Skeleton, { className: "h-7 w-64 bg-white/10" }), jsxs("div", { className: "flex items-center gap-4", children: [jsx(Skeleton, { className: "h-4 w-32 bg-white/10" }), jsx(Skeleton, { className: "h-4 w-28 bg-white/10" })] }), jsx(Skeleton, { className: "h-7 w-20 rounded-full bg-white/10 mt-1" })] })] }) }), tabs && tabs.length > 0 && (jsx("div", { className: "sticky top-0 z-10 flex items-center gap-6 sm:gap-8 border-b bg-card px-4 sm:px-6 h-[49px]", children: tabs.map((tab) => (jsxs("div", { className: "flex items-center gap-1.5 py-4", children: [jsx(Skeleton, { className: "h-4 w-4" }), jsx(Skeleton, { className: "h-4 w-16" })] }, tab.path))) })), jsx("div", { className: "bg-background flex-1 p-4 sm:p-6", children: jsxs("div", { className: "space-y-4", children: [jsx(Skeleton, { className: "h-8 w-48" }), jsx(Skeleton, { className: "h-5 w-72" }), jsx("div", { className: "grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-6", children: [1, 2, 3].map((i) => (jsx(Skeleton, { className: "h-32 rounded-xl" }, i))) })] }) })] }));
}

export { DetailPageHero };
//# sourceMappingURL=DetailPageHero.js.map
